/**
 * Calculate AI-powered valuation for a domain
 * @param {string} domainName - Full domain name (e.g., "TechStartup.io")
 * @returns {object} - Valuation with min, max, confidence, and breakdown
 */
export const calculateAIValuation = (domainName) => {
  const name = domainName.toLowerCase();
  const parts = name.split('.');
  const domain = parts[0];
  const tld = parts[1] || 'com';
  
  // Base scores
  let baseValue = 1000;
  let confidence = 70;
  
  // TLD scoring - premium TLDs worth more
  const tldValues = {
    'com': 1.5,
    'io': 1.3,
    'ai': 1.4,
    'co': 1.2,
    'net': 1.1,
    'org': 1.0,
    'app': 1.2,
    'dev': 1.1,
    'tech': 1.15,
    'xyz': 0.7,
  };
  const tldMultiplier = tldValues[tld] || 0.8;
  baseValue *= tldMultiplier;
  
  // Length scoring (optimal: 6-10 characters)
  const length = domain.length;
  let lengthScore = 5;
  
  if (length <= 5) {
    lengthScore = 5; // Short = premium
    baseValue *= 2;
    confidence += 5;
  } else if (length <= 10) {
    lengthScore = 4;
    baseValue *= 1.5;
  } else if (length <= 15) {
    lengthScore = 3;
    baseValue *= 1.2;
  } else {
    lengthScore = 2;
    baseValue *= 0.9;
    confidence -= 5;
  }
  
  // Keyword scoring - high-value keywords boost price
  const premiumKeywords = ['ai', 'tech', 'crypto', 'start', 'smart', 'cloud', 'data', 'app', 'web', 'digital', 'cyber', 'meta', 'nft', 'pay', 'shop'];
  const goodKeywords = ['hub', 'lab', 'pro', 'now', 'zone', 'base', 'link', 'media', 'network', 'solution', 'system', 'world', 'online'];
  
  let keywordScore = 3;
  let keywordMultiplier = 1;
  
  // Check for premium keywords
  for (const keyword of premiumKeywords) {
    if (domain.includes(keyword)) {
      keywordScore = 5;
      keywordMultiplier = 1.5;
      confidence += 5;
      break;
    }
  }
  
  // Check for good keywords if no premium found
  if (keywordScore === 3) {
    for (const keyword of goodKeywords) {
      if (domain.includes(keyword)) {
        keywordScore = 4;
        keywordMultiplier = 1.2;
        break;
      }
    }
  }
  
  baseValue *= keywordMultiplier;
  
  // Brandability (pronounceable, no numbers/hyphens)
  const hasNumbers = /\d/.test(domain);
  const hasHyphens = /-/.test(domain);
  let brandabilityScore = 5;
  
  if (hasNumbers) {
    brandabilityScore -= 2;
    baseValue *= 0.7;
    confidence -= 10;
  }
  if (hasHyphens) {
    brandabilityScore -= 2;
    baseValue *= 0.6;
    confidence -= 10;
  }
  
  // Ensure minimum score of 1
  brandabilityScore = Math.max(brandabilityScore, 1);
  
  // Memorability (vowel/consonant balance)
  const vowels = (domain.match(/[aeiou]/g) || []).length;
  const consonants = (domain.match(/[bcdfghjklmnpqrstvwxyz]/g) || []).length;
  const totalLetters = vowels + consonants;
  const ratio = totalLetters > 0 ? vowels / totalLetters : 0;
  
  let memorabilityScore = 3;
  if (ratio >= 0.3 && ratio <= 0.5) {
    memorabilityScore = 5;
    baseValue *= 1.1;
  } else if (ratio >= 0.2 && ratio <= 0.6) {
    memorabilityScore = 4;
  }
  
  // Market trend (simulate market conditions)
  const marketTrendScore = Math.floor(Math.random() * 2) + 3; // 3-4
  const trendMultiplier = marketTrendScore >= 4 ? 1.2 : 1;
  baseValue *= trendMultiplier;
  
  // TLD premium score (for display)
  const tldPremiumScore = Math.round((tldMultiplier / 1.5) * 5);
  
  // Calculate final range with variance
  const variance = 0.15; // ±15%
  const min = Math.round(baseValue * (1 - variance));
  const max = Math.round(baseValue * (1 + variance));
  
  // Overall score (weighted average)
  const overallScore = Math.round(
    (lengthScore * 0.2 + 
     keywordScore * 0.25 + 
     brandabilityScore * 0.25 + 
     memorabilityScore * 0.15 + 
     marketTrendScore * 0.15) * 20
  );
  
  // Ensure confidence is within bounds
  confidence = Math.min(Math.max(confidence, 60), 95);
  
  return {
    min,
    max,
    confidence,
    breakdown: {
      length: lengthScore,
      tldPremium: Math.min(tldPremiumScore, 5),
      keywordValue: keywordScore,
      brandability: brandabilityScore,
      memorability: memorabilityScore,
      marketTrend: marketTrendScore,
      overallScore: Math.min(overallScore, 100),
    }
  };
};

/**
 * Generate realistic price history for a domain
 * @param {number} currentPrice - Current domain price
 * @param {number} days - Number of days of history to generate
 * @returns {array} - Array of {date, price} objects
 */
export const generatePriceHistory = (currentPrice, days = 30) => {
  const history = [];
  let price = currentPrice * 0.9; // Start 10% lower to show growth
  
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    // Random walk with slight upward trend
    const change = (Math.random() - 0.45) * 0.1; // Slightly biased upward
    price = price * (1 + change);
    
    // Ensure price doesn't go below 20% of current or above 180%
    price = Math.max(currentPrice * 0.2, Math.min(currentPrice * 1.8, price));
    
    history.push({
      date: date.toISOString().split('T')[0],
      price: Math.round(price),
    });
  }
  
  // Force last price to match current price
  history[history.length - 1].price = currentPrice;
  
  return history;
};

/**
 * Suggest optimal listing price based on AI valuation
 * @param {object} aiValuation - Result from calculateAIValuation
 * @returns {number} - Suggested listing price
 */
export const suggestListingPrice = (aiValuation) => {
  const { min, max, confidence } = aiValuation;
  
  // If high confidence, suggest closer to max
  if (confidence >= 85) {
    return Math.round((max * 0.7 + min * 0.3));
  } else if (confidence >= 75) {
    return Math.round((max + min) / 2);
  } else {
    // Low confidence, suggest closer to min
    return Math.round((max * 0.3 + min * 0.7));
  }
};

/**
 * Suggest bid range for a domain
 * @param {number} askingPrice - Current asking price
 * @param {object} aiValuation - AI valuation
 * @returns {object} - {min, max, optimal}
 */
export const suggestBidRange = (askingPrice, aiValuation) => {
  const { min: aiMin, max: aiMax } = aiValuation;
  
  // Suggest bidding between 80-95% of asking price
  // But consider AI valuation
  const minBid = Math.round(Math.min(askingPrice * 0.8, aiMin * 0.95));
  const maxBid = Math.round(Math.min(askingPrice * 0.95, aiMax * 1.05));
  const optimalBid = Math.round((minBid + maxBid) / 2);
  
  return {
    min: minBid,
    max: maxBid,
    optimal: optimalBid,
  };
};
